import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Download, QrCode, Mail, Palette, Eye } from 'lucide-react';
import { AppleIcon, AndroidIcon } from '@/components/wallet/WalletIcons';

interface LoyaltyProgram {
  id: string;
  name: string;
  description: string;
  program_type: 'points' | 'stamps' | 'visits';
  reward_description: string;
  is_active: boolean;
}

interface UnifiedPassBuilderProps {
  businessId: string;
  loyaltyPrograms: LoyaltyProgram[];
  selectedProgramId?: string;
  onPassGenerated?: (passData: any) => void;
}

const UnifiedPassBuilder: React.FC<UnifiedPassBuilderProps> = ({ 
  businessId, 
  loyaltyPrograms,
  selectedProgramId,
  onPassGenerated 
}) => {
  const { session } = useAuth();

  // Selected loyalty program - auto-select if programId is provided
  const [selectedProgram, setSelectedProgram] = useState<LoyaltyProgram | null>(() => {
    if (selectedProgramId) {
      return loyaltyPrograms.find(p => p.id === selectedProgramId) || null;
    }
    return null;
  });
  
  // Pass design state
  const [passDesign, setPassDesign] = useState({
    businessName: '',
    logoUrl: '',
    backgroundColor: '#1976d2',
    textColor: '#ffffff',
    cardTitle: '',
    headerText: ''
  });

  // Customer data for individual pass
  const [customerData, setCustomerData] = useState({
    customerId: '',
    customerName: '',
    currentPoints: 0,
    membershipLevel: 'Bronze'
  });

  // Generated passes state
  const [generatedPasses, setGeneratedPasses] = useState({
    googleClass: null,
    googleObject: null,
    googleJWT: null,
    applePass: null
  });

  // UI state
  const [activeTab, setActiveTab] = useState('design');
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);

  const addStatusMessage = (message: string) => {
    setStatusMessages(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearStatusMessages = () => {
    setStatusMessages([]);
  };

  // Auto-select program when prop changes
  useEffect(() => {
    if (selectedProgramId) {
      const program = loyaltyPrograms.find(p => p.id === selectedProgramId);
      if (program) {
        setSelectedProgram(program);
      }
    }
  }, [selectedProgramId, loyaltyPrograms]);

  // Auto-populate design from selected loyalty program
  useEffect(() => {
    if (selectedProgram) {
      setPassDesign(prev => ({
        ...prev,
        cardTitle: selectedProgram.name,
        headerText: selectedProgram.description || `${selectedProgram.name} Loyalty Card`
      }));
    }
  }, [selectedProgram]);

  const generateUnifiedPasses = async () => {
    if (!selectedProgram || !session?.access_token || !businessId) {
      addStatusMessage('❌ Missing required data: program, authentication, or business ID');
      return;
    }

    setIsGenerating(true);
    clearStatusMessages();
    addStatusMessage('🚀 Starting unified pass generation...');

    try {
      // Step 1: Generate Google Wallet Class
      addStatusMessage('📱 Creating Google Wallet class...');
      const classResponse = await fetch('http://localhost:3001/google-wallet/create-loyalty-class', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          class_suffix: `${selectedProgram.id}-${Date.now()}`,
          class_data: {
            name: passDesign.cardTitle || selectedProgram.name,
            loyaltyProgramName: selectedProgram.name,
            issuerName: passDesign.businessName || 'TagInn Business',
            logoUrl: passDesign.logoUrl
          }
        }),
      });

      const classData = await classResponse.json();
      if (!classResponse.ok) throw new Error(classData.error);
      
      addStatusMessage('✅ Google Wallet class created');
      setGeneratedPasses(prev => ({ ...prev, googleClass: classData }));

      // Step 2: Generate Google Wallet Object
      addStatusMessage('🎫 Creating Google Wallet object...');
      const objectResponse = await fetch('http://localhost:3001/google-wallet/create-loyalty-object', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          class_suffix: classData.id.split('.')[1],
          object_suffix: `${customerData.customerId || 'customer'}-${Date.now()}`,
          object_data: {
            cardTitle: passDesign.cardTitle || selectedProgram.name,
            header: passDesign.headerText || passDesign.businessName
          }
        }),
      });

      const objectData = await objectResponse.json();
      if (!objectResponse.ok) throw new Error(objectData.error);
      
      addStatusMessage('✅ Google Wallet object created');
      setGeneratedPasses(prev => ({ ...prev, googleObject: objectData }));

      // Step 3: Generate Google Wallet JWT
      addStatusMessage('🔐 Generating Google Wallet JWT...');
      const jwtResponse = await fetch('http://localhost:3001/google-wallet/generate-jwt', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          loyalty_object_id: objectData.id
        }),
      });

      const jwtData = await jwtResponse.json();
      if (!jwtResponse.ok) throw new Error(jwtData.error);
      
      addStatusMessage('✅ Google Wallet JWT generated');
      setGeneratedPasses(prev => ({ ...prev, googleJWT: jwtData.jwt }));

      // Step 4: Generate Apple Wallet Pass
      addStatusMessage('🍎 Creating Apple Wallet pass...');
      const appleResponse = await fetch('http://localhost:3001/generate-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: customerData.customerId || `customer-${Date.now()}`,
          businessName: passDesign.businessName || selectedProgram.name,
          loyaltyPoints: customerData.currentPoints || 0
        }),
      });

      if (!appleResponse.ok) {
        const errorData = await appleResponse.json();
        throw new Error(errorData.error);
      }

      const appleBlob = await appleResponse.blob();
      addStatusMessage('✅ Apple Wallet pass generated');
      setGeneratedPasses(prev => ({ ...prev, applePass: appleBlob }));

      addStatusMessage('🎉 All passes generated successfully!');
      
      // Callback for parent component
      if (onPassGenerated) {
        onPassGenerated({
          program: selectedProgram,
          design: passDesign,
          customer: customerData,
          passes: { ...generatedPasses, applePass: appleBlob }
        });
      }

    } catch (error: unknown) {
      addStatusMessage(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadApplePass = () => {
    if (generatedPasses.applePass) {
      const url = URL.createObjectURL(generatedPasses.applePass);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${passDesign.cardTitle || 'loyalty'}_card.pkpass`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const addToGoogleWallet = () => {
    if (generatedPasses.googleJWT) {
      // Create the official Google Wallet URL
      const googleWalletUrl = `https://pay.google.com/gp/v/save/${generatedPasses.googleJWT}`;
      
      // For mobile devices, try to open directly
      if (window.navigator.userAgent.match(/Android/i)) {
        window.location.href = googleWalletUrl;
      } else {
        // For desktop, open in new tab with instructions
        window.open(googleWalletUrl, '_blank');
      }
      
      addStatusMessage('🔗 Google Wallet link opened - follow prompts to add pass');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Unified Digital Wallet Pass Builder
          </CardTitle>
          <CardDescription>
            Create Apple Wallet and Google Pay passes from your loyalty programs with one click
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="design">📱 Design</TabsTrigger>
              <TabsTrigger value="customer">👤 Customer</TabsTrigger>
              <TabsTrigger value="generate">⚡ Generate</TabsTrigger>
              <TabsTrigger value="distribute">📤 Distribute</TabsTrigger>
            </TabsList>

            {/* Design Tab */}
            <TabsContent value="design" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Select Loyalty Program</h3>
                  <div className="space-y-2">
                    {loyaltyPrograms.filter(p => p.is_active).map(program => (
                      <Card 
                        key={program.id} 
                        className={`cursor-pointer transition-colors ${
                          selectedProgram?.id === program.id ? 'ring-2 ring-blue-500' : ''
                        }`}
                        onClick={() => setSelectedProgram(program)}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium">{program.name}</h4>
                              <p className="text-sm text-gray-600">{program.description}</p>
                            </div>
                            <Badge>{program.program_type}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {selectedProgram && (
                    <div className="space-y-4 border-t pt-4">
                      <h4 className="font-medium">Pass Customization</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="businessName">Business Name</Label>
                          <Input
                            id="businessName"
                            value={passDesign.businessName}
                            onChange={(e) => setPassDesign(prev => ({ ...prev, businessName: e.target.value }))}
                            placeholder="Your Business Name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="logoUrl">Logo URL (optional)</Label>
                          <Input
                            id="logoUrl"
                            value={passDesign.logoUrl}
                            onChange={(e) => setPassDesign(prev => ({ ...prev, logoUrl: e.target.value }))}
                            placeholder="https://example.com/logo.png"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cardTitle">Card Title</Label>
                          <Input
                            id="cardTitle"
                            value={passDesign.cardTitle}
                            onChange={(e) => setPassDesign(prev => ({ ...prev, cardTitle: e.target.value }))}
                            placeholder="Loyalty Card"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Live Preview */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Live Preview
                  </h3>
                  <div className="space-y-4">
                    {/* Google Pay Preview */}
                    <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <AndroidIcon className="h-6 w-6" />
                          <span className="text-xs opacity-80">Google Pay</span>
                        </div>
                        <h4 className="font-bold">{passDesign.cardTitle || 'Loyalty Card'}</h4>
                        <p className="text-sm opacity-90">{passDesign.businessName || 'Business Name'}</p>
                        <div className="mt-4 p-2 bg-white/20 rounded">
                          <p className="text-xs">Points: {customerData.currentPoints}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Apple Wallet Preview */}
                    <Card className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <AppleIcon className="h-6 w-6" />
                          <span className="text-xs opacity-80">Apple Wallet</span>
                        </div>
                        <h4 className="font-bold">{passDesign.cardTitle || 'Loyalty Card'}</h4>
                        <p className="text-sm opacity-90">{passDesign.businessName || 'Business Name'}</p>
                        <div className="mt-4 p-2 bg-white/20 rounded">
                          <p className="text-xs">Points: {customerData.currentPoints}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Customer Tab */}
            <TabsContent value="customer" className="space-y-4">
              <h3 className="text-lg font-semibold">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customerId">Customer ID</Label>
                  <Input
                    id="customerId"
                    value={customerData.customerId}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, customerId: e.target.value }))}
                    placeholder="CUST123 or auto-generate"
                  />
                </div>
                <div>
                  <Label htmlFor="customerName">Customer Name</Label>
                  <Input
                    id="customerName"
                    value={customerData.customerName}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="currentPoints">Current Points</Label>
                  <Input
                    id="currentPoints"
                    type="number"
                    value={customerData.currentPoints}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, currentPoints: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="membershipLevel">Membership Level</Label>
                  <Input
                    id="membershipLevel"
                    value={customerData.membershipLevel}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, membershipLevel: e.target.value }))}
                    placeholder="Bronze, Silver, Gold"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Generate Tab */}
            <TabsContent value="generate" className="space-y-4">
              <div className="text-center space-y-4">
                <h3 className="text-lg font-semibold">Generate Digital Wallet Passes</h3>
                <p className="text-gray-600">
                  Create both Google Pay and Apple Wallet passes simultaneously
                </p>
                
                <Button 
                  onClick={generateUnifiedPasses}
                  disabled={!selectedProgram || isGenerating}
                  size="lg"
                  className="w-full md:w-auto"
                >
                  {isGenerating ? '⚡ Generating...' : '🚀 Generate Both Passes'}
                </Button>

                {statusMessages.length > 0 && (
                  <Card className="text-left">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium">Generation Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {statusMessages.map((message, index) => (
                          <p key={index} className="text-xs font-mono">{message}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Distribute Tab */}
            <TabsContent value="distribute" className="space-y-4">
              <h3 className="text-lg font-semibold">Pass Distribution</h3>
              
              {(generatedPasses.googleJWT || generatedPasses.applePass) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Google Pay Distribution */}
                  {generatedPasses.googleJWT && (
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <AndroidIcon className="h-5 w-5" />
                          Google Pay
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button onClick={addToGoogleWallet} className="w-full">
                          Add to Google Wallet
                        </Button>
                        <div className="text-xs text-gray-500 break-all bg-gray-50 p-2 rounded">
                          JWT: {generatedPasses.googleJWT.substring(0, 50)}...
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Apple Wallet Distribution */}
                  {generatedPasses.applePass && (
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <AppleIcon className="h-5 w-5" />
                          Apple Wallet
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button onClick={downloadApplePass} className="w-full">
                          <Download className="h-4 w-4 mr-2" />
                          Download .pkpass
                        </Button>
                        <p className="text-xs text-gray-500">
                          Pass ready for download and installation
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <p className="text-gray-500">
                      Generate passes first to see distribution options
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedPassBuilder;